import { Button } from '@mui/material';
import { useContext, useState } from 'react';
import handleShare from '../../../../common/handleShare';
import { DEFAULT_GROUP_COVER } from '../../../../constant/imgImport';
import useUpdateCover from '../../../../hooks/profile/business/useUpdateCover';
import {
  IUserProfileData,
  RoleViewEnum,
} from '../../../../interfaces/profile.interface';
import { useModal } from '../../../../state/modal-provider/ModalProvider';
import Cover from '../../../image/Cover';
import EditBusinessInfoModal from '../../../modal/training-module/EditBusinessInfoModal';
import { OutlinedPencil, OutlinedShare } from '../../svgIcon/outlined';
import BlueTick from '../BlueTick';
import BusinessDescription from './BusinessDescription';
import BusinessFollowButton from './BusinessFollowButton';
import BusinessInfoAvatar from './BusinessInfoAvatar';
import { BusinessProfileModeContext } from './BusinessProfileModeProvider';

const BusinessInfo = () => {
  const [openEditModal, setOpenEditModal] = useState(false);
  const { data } = useContext(BusinessProfileModeContext);
  const {
    mutateReturn: { mutateAsync },
  } = useUpdateCover();
  const roleView = (data as IUserProfileData)?.viewPermission;
  const coverUrl = (data as IUserProfileData)?.profiles?.cover;
  const avatarUrl = (data as IUserProfileData)?.profiles?.avatar;
  const name = (data as IUserProfileData)?.profiles?.qbankData?.fullName;
  const description = (data as IUserProfileData)?.profiles?.bio;
  const hasBlueTick = (data as IUserProfileData)?.profiles?.hasBlueTick;
  const userId = `${data?.profiles?.ownerId}`;
  const isFollowed = data?.viewPermission === RoleViewEnum.Follow;
  const isOwner = roleView == RoleViewEnum.Owner;

  const toggleEditModal = () => {
    setOpenEditModal((_) => !_);
  };

  return (
    <div className="w-full">
      {/* cover and avatar */}
      <div className="relative">
        <Cover
          value={coverUrl || DEFAULT_GROUP_COVER}
          onChange={mutateAsync}
          canEdit={isOwner}
          bottomClassname={'-mx-4 md:mx-0'}
        />
        <BusinessInfoAvatar canEdit={isOwner} avatarUrl={avatarUrl} />
      </div>
      {/* name, description and share button */}
      {/* check avatar img at BusinessInfoAvatar */}
      <div className="mt-20 flex max-w-[100%] flex-col items-start justify-between gap-x-14 gap-y-4 md:mt-5 lg:mt-24 lg:flex-row xl:ml-auto xl:mt-5 xl:max-w-[calc(100%-164px)] xl:gap-x-16 xl:pl-20">
        <div className="ml-auto hidden md:block lg:hidden">
          <CTAButton
            name={name}
            userId={userId}
            isFollowed={isFollowed}
            showFollowButton={!isOwner}
          />
        </div>

        <div className="group group mt-5 flex flex-1 items-start text-sm font-medium leading-[120%] text-slate_gray lg:mt-0">
          <div className="flex flex-1 flex-col">
            <div className="mb-2 flex w-fit items-start justify-between gap-2">
              <p className="text-2xl font-bold leading-[140%] tracking-[-0.4px] text-Text/Lightbackground/Primary">
                {data?.profiles.title && <>{data?.profiles.title}. </>}
                {name}
              </p>
              {hasBlueTick && (
                <div className="mt-1 text-2xl leading-[140%]">
                  <BlueTick scale="scale-100" />
                </div>
              )}
            </div>
            <BusinessDescription
              description={description}
              canEdit={isOwner}
              onOpenEditInfoModal={toggleEditModal}
            />
          </div>
          {isOwner && (
            <Button
              onClick={isOwner ? toggleEditModal : undefined}
              startIcon={<OutlinedPencil />}
              className={`invisible !flex !w-fit !flex-shrink-0 !text-sm !font-semibold !normal-case !leading-[16px] !tracking-[-0.1px] !text-Primary/Main hover:!bg-Action/Hover active:!bg-transparent group-hover:visible`}
            >
              <p className="hidden md:block">Chỉnh sửa</p>
            </Button>
          )}
        </div>

        <div className="block md:hidden lg:block">
          <CTAButton
            name={name}
            userId={userId}
            isFollowed={isFollowed}
            showFollowButton={!isOwner}
          />
        </div>
      </div>

      <EditBusinessInfoModal
        onClose={toggleEditModal}
        open={openEditModal}
        defaultData={{
          name,
          description,
        }}
      />
    </div>
  );
};

const CTAButton = ({
  name,
  userId,
  isFollowed,
  showFollowButton,
}: {
  name: string;
  userId: string;
  isFollowed: boolean;
  showFollowButton: boolean;
}) => {
  const { toast } = useModal();
  return (
    <div className="flex items-center justify-between gap-2">
      {showFollowButton && (
        <BusinessFollowButton
          bizProfileId={userId}
          isFollowed={isFollowed}
          showIcon
          props={{
            defaultComponent: 'button',
            props: {
              variant: 'high_emphasis_v1',
              sx: {
                borderRadius: '0.5rem',
                paddingTop: '10px',
                paddingBottom: '10px',
              },
            },
          }}
        />
      )}
      <Button
        onClick={() => handleShare(name, toast)}
        className="!cursor-pointer !justify-between !self-end !rounded-lg !border !border-solid !border-Other/Divider !bg-transparent !p-[10px_16px] !normal-case !text-Primary/Pressed hover:!bg-[#009FE499] hover:!text-Text/Darkbackground/Primary lg:!self-auto"
      >
        <OutlinedShare />
        <div className="pl-1 text-sm font-semibold leading-[16px]">Chia sẻ</div>
      </Button>
    </div>
  );
};

export default BusinessInfo;
